import {useCallback,useEffect,useRef,useState} from 'react';
import {useExamAccess} from './ExamAccessProvider';
import {attendanceMessage,attendanceRequest} from './attendanceClient';

const phaseOrder={open:0,sealed:1,completed:2};
const mergeSession=(current,update)=>({...current,...update,phase:phaseOrder[update.phase]<phaseOrder[current.phase]?current.phase:(update.phase || current.phase)});

export default function useExamAttendanceSession(examId,submitting) {
    const access=useExamAccess(),accessRef=useRef(access);accessRef.current=access;
    const scopeKey=JSON.stringify([access?.uid || '',examId]),scopeRef=useRef({key:scopeKey});
    if(scopeRef.current.key!==scopeKey)scopeRef.current={key:scopeKey};
    const scope=scopeRef.current;
    const [state,setState]=useState({scope,session:null,connectionError:''});
    const sessionRef=useRef({scope,value:null}),beginRequest=useRef(null),mounted=useRef(true);
    if(sessionRef.current.scope!==scope)sessionRef.current={scope,value:null};
    const session=state.scope===scope?state.session:null;
    const connectionError=state.scope===scope?state.connectionError:'';
    useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
    const currentSession=useCallback(()=>scopeRef.current===scope?sessionRef.current.value:null,[scope]);
    const accept=useCallback(value=>{
        if(!mounted.current || scopeRef.current!==scope)return false;
        sessionRef.current={scope,value};
        setState(previous=>({scope,session:value,connectionError:previous.scope===scope?previous.connectionError:''}));
        return true;
    },[scope]);
    const begin=useCallback(async()=>{
        if(scopeRef.current!==scope)return null;
        if(!accessRef.current?.student || !accessRef.current.pending?.some(p=>p.examId===examId)){accept(null);return null;}
        if(beginRequest.current?.scope===scope)return beginRequest.current.promise;
        const request={scope};
        request.promise=attendanceRequest('begin',{examId}).then(value=>{
            const next=value.managed?value:null;
            return accept(next)?next:null;
        }).finally(()=>{if(beginRequest.current===request)beginRequest.current=null;});
        beginRequest.current=request;
        return request.promise;
    },[examId,scope,accept]);
    useEffect(()=>{
        if(!session?.attemptId || session.phase!=='open' || submitting)return;
        let active=true,busy=false;
        const pulse=async()=>{
            if(busy)return;busy=true;
            try{
                const result=await attendanceRequest('pulse',{examId,attemptId:session.attemptId,visible:document.visibilityState==='visible'});
                const current=currentSession();
                if(active && current?.attemptId===session.attemptId){
                    accept(mergeSession(current,result));
                    setState(previous=>({...previous,connectionError:''}));
                }
            }catch{
                if(active && currentSession()?.attemptId===session.attemptId)setState(previous=>({...previous,connectionError:'Your exam connection is reconnecting. Reconnect before submitting.'}));
            }finally{busy=false;}
        };
        pulse();const timer=setInterval(pulse,15000);document.addEventListener('visibilitychange',pulse);window.addEventListener('online',pulse);
        return()=>{active=false;clearInterval(timer);document.removeEventListener('visibilitychange',pulse);window.removeEventListener('online',pulse);};
    },[examId,session?.attemptId,session?.phase,submitting,currentSession,accept]);
    const seal=useCallback(async(answers,flaggedQuestions)=>{
        const current=currentSession();if(!current)return null;
        if(current.phase==='sealed' || current.phase==='completed')return {phase:current.phase};
        const result=await attendanceRequest('seal',{examId,attemptId:current.attemptId,answers,flaggedQuestions,visible:document.visibilityState==='visible'});
        const latest=currentSession();
        if(latest?.attemptId===current.attemptId)accept(mergeSession(latest,{...result,phase:result.phase || 'sealed'}));
        return result;
    },[examId,currentSession,accept]);
    const complete=useCallback(async result=>{
        const current=currentSession();if(!current)return null;
        const {submittedAt,answers,flaggedQuestions,...marks}=result;
        const item=accessRef.current?.pending?.find(p=>p.examId===examId);
        const response=await attendanceRequest('complete',{examId,attemptId:current.attemptId,result:marks});
        if(currentSession()?.attemptId!==current.attemptId)return {...response,blocked:true};
        accept({...current,phase:'completed'});
        if(response.status==='completed' && item && current.phase!=='completed')accessRef.current?.showCompletion?.(attendanceMessage(item.messages.successMessage,item,accessRef.current?.studentName));
        let verified=null,refreshTimer;
        try{
            verified=await Promise.race([
                Promise.resolve().then(()=>accessRef.current?.refresh(true)),
                new Promise(resolve=>{refreshTimer=setTimeout(()=>resolve(null),10000);})
            ]);
        }catch{/* The attempt is already saved; access can reconnect on the dashboard. */}
        finally{clearTimeout(refreshTimer);}
        return {...response,blocked:verified?.blocked!==false,accessRefreshPending:verified?false:true};
    },[examId,currentSession,accept]);
    const item=access?.pending?.find(p=>p.examId===examId);
    return {session,begin,seal,complete,connectionError,clockOffset:Number(access?.offset)||0,resumeMessage:item?attendanceMessage(item.messages.resumeMessage,item,access?.studentName):''};
}
